// Contenu FR de l'Académie de l'IA (ton Cocoon). Structure par profil d'âge.
// Chaque module : sections (texte), quiz (vrai/faux), sandbox (bac à sable de prompt).

export const PROFILES = [
  { key: "junior", label: "Junior", age: "8–12 ans", icon: "Baby", free: true },
  { key: "ado", label: "Ado", age: "13–17 ans", icon: "Rocket", free: true },
  { key: "adulte", label: "Adulte / Pro", age: "18 ans et +", icon: "Briefcase", free: false },
];

export const MODULES = {
  junior: [
    {
      id: "junior-robot",
      title: "C'est quoi un robot intelligent ?",
      desc: "La différence entre une machine qui obéit et une IA qui apprend.",
      minutes: 6,
      sections: [
        { h: "Une machine qui suit des règles", p: "Un grille-pain fait toujours la même chose : tu appuies, il grille. Il ne réfléchit pas, il **suit une règle**. La plupart des machines fonctionnent comme ça." },
        { h: "Une IA qui apprend d'exemples", p: "Une intelligence artificielle, elle, **apprend en regardant plein d'exemples**. Pour reconnaître un chat, on lui montre des milliers de photos de chats… jusqu'à ce qu'elle devine toute seule sur une photo qu'elle n'a jamais vue." },
        { h: "La grande différence", p: "La règle est écrite par un humain à l'avance. L'IA, elle, **fabrique sa propre règle** à partir des exemples. C'est ça, « apprendre »." },
      ],
      quiz: [
        { q: "Une IA apprend en regardant beaucoup d'exemples.", answer: true, explain: "Oui ! Elle s'entraîne sur des milliers d'exemples pour deviner toute seule ensuite." },
        { q: "Un grille-pain est une intelligence artificielle.", answer: false, explain: "Non : il suit une règle fixe, il n'apprend rien." },
        { q: "L'IA fabrique sa propre règle à partir des exemples.", answer: true, explain: "Exactement, c'est ce qui la rend « intelligente »." },
      ],
    },
    {
      id: "junior-images",
      title: "L'IA et mes images",
      desc: "Comment l'IA fabrique des images, et comment repérer un trucage.",
      minutes: 6,
      sections: [
        { h: "Des images inventées", p: "Certaines IA peuvent **inventer une image** à partir de mots : « un chat astronaute sur la Lune » → et hop, une image apparaît. Mais elle n'est **pas réelle** : personne ne l'a photographiée." },
        { h: "Repérer les petits pièges", p: "Les images d'IA se trompent souvent sur les **détails** : des mains à 6 doigts, du texte bizarre, des reflets impossibles. Ouvre l'œil !" },
        { h: "Le droit à l'image", p: "On ne fabrique pas l'image de quelqu'un sans son accord. Une image, même inventée, **ça se respecte**." },
      ],
      quiz: [
        { q: "Une image générée par IA est toujours une vraie photo.", answer: false, explain: "Non : elle est inventée, personne ne l'a prise en vrai." },
        { q: "Compter les doigts sur une main peut aider à repérer un trucage.", answer: true, explain: "Oui ! L'IA se trompe souvent sur les mains et les détails." },
        { q: "On peut fabriquer l'image de n'importe qui sans lui demander.", answer: false, explain: "Non : le droit à l'image, ça se respecte." },
      ],
    },
  ],
  ado: [
    {
      id: "ado-revisions",
      title: "Booster ses révisions avec l'IA (sans tricher)",
      desc: "Faire de l'IA ton tuteur personnel — et t'entraîner au Bac à Sable de prompt.",
      minutes: 12,
      flagship: true,
      sandbox: true,
      sections: [
        { h: "L'IA, un tuteur, pas un raccourci", p: "Copier-coller une réponse, c'est se tirer une balle dans le pied le jour du contrôle. Utilise l'IA pour **comprendre**, pas pour éviter de réfléchir : demande-lui de t'**expliquer**, de te **poser des questions**, de te faire des **fiches**." },
        { h: "3 usages malins", p: "**1.** « Explique-moi la photosynthèse comme si j'avais 12 ans. » **2.** « Pose-moi 5 questions de révision sur la Révolution française. » **3.** « Transforme ce cours en fiche de synthèse en 8 points. »" },
        { h: "La règle d'or", p: "Vérifie toujours : l'IA peut se tromper avec assurance (on appelle ça une **hallucination**). Croise avec ton cours. Le savoir reste dans **ta** tête, pas dans la sienne." },
      ],
      quiz: [
        { q: "Demander à l'IA d'expliquer un cours autrement, c'est tricher.", answer: false, explain: "Non : comprendre autrement, c'est apprendre. Tricher, c'est rendre son travail sans réfléchir." },
        { q: "L'IA peut donner une réponse fausse avec assurance.", answer: true, explain: "Oui : ça s'appelle une hallucination. Vérifie toujours." },
        { q: "Un bon usage : lui demander de te poser des questions de révision.", answer: true, explain: "Excellent réflexe pour t'auto-tester !" },
      ],
    },
    {
      id: "ado-deepfakes",
      title: "Deepfakes, fake news & éthique",
      desc: "Comprendre les modèles génératifs et vérifier les sources.",
      minutes: 9,
      sections: [
        { h: "Texte, image, audio, vidéo", p: "Les modèles génératifs produisent aujourd'hui des **voix**, des **visages** et des **vidéos** hyper réalistes. Un deepfake peut faire « dire » n'importe quoi à n'importe qui." },
        { h: "Le réflexe anti-intox", p: "Devant une info choc : **qui la publie ?** **quelles preuves ?** **d'autres sources sérieuses la confirment-elles ?** Le doute est ton meilleur allié." },
        { h: "Ton pouvoir", p: "Ne partage pas avant de vérifier. Une intox partagée, c'est une intox qui gagne." },
      ],
      quiz: [
        { q: "Une vidéo peut être entièrement fabriquée par une IA.", answer: true, explain: "Oui : c'est un deepfake, de plus en plus réaliste." },
        { q: "Si une info est choquante, il faut la partager tout de suite.", answer: false, explain: "Non : vérifie d'abord la source et les preuves." },
      ],
    },
    {
      id: "ado-creation",
      title: "S'initier à la création (image & audio)",
      desc: "Découvrir le prompt visuel et sonore, pas à pas.",
      minutes: 8,
      sandbox: true,
      sections: [
        { h: "Créer avec des mots", p: "Générer une image ou une musique, c'est décrire précisément ce que tu veux : le **sujet**, le **style**, l'**ambiance**, les **couleurs**. Plus tu es précis, plus le résultat te ressemble." },
        { h: "Itérer, c'est normal", p: "Le premier essai est rarement le bon. On **ajuste** : « plus lumineux », « style aquarelle », « ambiance mystérieuse ». La création, c'est du dialogue." },
      ],
      quiz: [
        { q: "Un prompt précis donne un meilleur résultat.", answer: true, explain: "Oui : sujet + style + ambiance + couleurs." },
        { q: "Il faut toujours garder le tout premier résultat.", answer: false, explain: "Non : on itère et on affine, c'est le jeu." },
      ],
    },
  ],
  adulte: [
    {
      id: "adulte-prompt",
      title: "Maîtriser le Prompt Engineering",
      desc: "Rôle · Contexte · Tâche · Format · Contraintes — la structure qui change tout.",
      minutes: 12,
      sandbox: true,
      sections: [
        { h: "La structure RCTFC", p: "Un prompt efficace tient en 5 briques : **Rôle** (« Tu es un juriste… »), **Contexte** (ta situation), **Tâche** (ce que tu veux), **Format** (liste, tableau, e-mail…), **Contraintes** (ton, longueur, ce qu'il faut éviter)." },
        { h: "Itérer intelligemment", p: "La première réponse est un brouillon. Corrige-la : « plus court », « ajoute un exemple », « adopte un ton plus formel ». Tu pilotes." },
        { h: "Éviter la flatterie", p: "Demande à l'IA de te **contredire** et de pointer les faiblesses de ton raisonnement. Un bon prompt cherche la vérité, pas l'approbation." },
      ],
      quiz: [
        { q: "Donner un rôle à l'IA améliore la réponse.", answer: true, explain: "Oui : « Tu es un expert en… » cadre le registre et la précision." },
        { q: "La première réponse de l'IA est forcément la meilleure.", answer: false, explain: "Non : on itère, on corrige, on affine." },
        { q: "Préciser le format attendu fait partie d'un bon prompt.", answer: true, explain: "Tableau, liste, e-mail… le format compte autant que la tâche." },
      ],
    },
    {
      id: "adulte-productivite",
      title: "L'IA au service de sa productivité",
      desc: "E-mails, synthèses, présentations, analyse de données.",
      minutes: 10,
      sections: [
        { h: "Déléguer l'ingrat, garder le noble", p: "Fais rédiger les brouillons d'e-mails, résumer les longs documents, préparer les trames de présentation. Tu gardes le **jugement**, la **décision**, la **relation**." },
        { h: "Cas d'usage concrets", p: "« Résume ce rapport de 20 pages en 10 points d'action. » « Rédige 3 versions de cet e-mail : formelle, chaleureuse, directe. » « Propose un plan de présentation pour ce sujet. »" },
      ],
      quiz: [
        { q: "L'IA doit prendre les décisions importantes à ta place.", answer: false, explain: "Non : elle assiste, tu décides. Le jugement reste humain." },
        { q: "Résumer un long document est un bon cas d'usage.", answer: true, explain: "Oui : gain de temps considérable, à condition de vérifier." },
      ],
    },
    {
      id: "adulte-securite",
      title: "Sécurité, données & droit de l'IA",
      desc: "RGPD / AI Act, propriété intellectuelle, pièges à éviter.",
      minutes: 10,
      sections: [
        { h: "Tes données sont précieuses", p: "Ne colle jamais de **données confidentielles** (clients, santé, mots de passe) dans un outil grand public : elles peuvent être stockées ou réutilisées. Le RGPD et l'AI Act encadrent ces usages." },
        { h: "Propriété & responsabilité", p: "Le contenu généré peut poser des questions de **droits d'auteur**. Et surtout : **tu restes responsable** de ce que tu publies, hallucinations comprises." },
      ],
      quiz: [
        { q: "On peut coller des données clients confidentielles dans n'importe quel outil IA.", answer: false, explain: "Non : risque de fuite. Reste prudent avec les données sensibles." },
        { q: "Tu restes responsable de ce que tu publies, même généré par IA.", answer: true, explain: "Oui : vérifie toujours avant de diffuser." },
      ],
    },
  ],
};
